import { z } from "zod";

/**
 * SessionTokenReader answers the session token current at the moment a request is made, or
 * undefined where none is held. The hosted embed holds one: its token lives minutes and the page
 * that frames it hands it a new one before expiry, so the strategy reads it per request rather
 * than fixing the token it was built with.
 */
export type SessionTokenReader = () => string | undefined;

/** isSessionTokenReader narrows a strategy member to a {@link SessionTokenReader}. */
const isSessionTokenReader = (value: unknown): value is SessionTokenReader =>
  typeof value === "function";

/**
 * authStrategySchema is the single source of truth for how the REST client
 * authenticates a request. It is a discriminated union (on `kind`):
 *
 * - `session`: the authenticated webapp path — the Ory session rides the httpOnly
 *   ory_session_* cookie, so the client sends `credentials: "include"` and no
 *   auth headers (the SPA cannot read the cookie to build a Bearer header).
 * - `embed`: the anonymous, org-scoped iframe path — the publishable key (and an
 *   optional session token for require_session_token orgs) ride the X-API-Key /
 *   X-Session-Token headers and NO cookie (sidestepping cross-origin ITP). The
 *   session token is either the string the embed's address carried, or a reader
 *   ({@link SessionTokenReader}) called for each request, so a token the hosted embed
 *   renews reaches its next read and its next tile without anything being rebuilt.
 * - `share`: the resource-scoped share-link path — the share token rides the
 *   X-Share-Token header and authorizes exactly the one shared itinerary (its detail,
 *   tiles, and export) and nothing else, regardless of whether the visitor is signed
 *   in. It ALSO sends `credentials: "include"` so a SIGNED-IN visitor's Ory cookie
 *   rides alongside the token: the backend reads the session ONLY to resolve the
 *   viewer's own plan (the share view gates the Pro-only elevation analysis by the
 *   viewer's plan, exactly as /studio does), never for resource access — the share
 *   token stays the sole authorization. An anonymous visitor sends no cookie and reads
 *   as free.
 *
 * Zod-first so the strategy is validated where it is constructed (the route
 * loader) and the AuthStrategy type is derived from it.
 */
export const authStrategySchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("session") }),
  z.object({
    kind: z.literal("embed"),
    publishableKey: z.string(),
    sessionToken: z
      .union([z.string(), z.custom<SessionTokenReader>(isSessionTokenReader)])
      .optional(),
  }),
  z.object({ kind: z.literal("share"), shareToken: z.string() }),
]);

/**
 * AuthStrategy is how the REST client authenticates a request, derived from
 * authStrategySchema.
 */
export type AuthStrategy = z.infer<typeof authStrategySchema>;

/**
 * SESSION_AUTH is the default session strategy singleton — the webapp path. It is
 * a frozen value so callers that do not embed share one reference.
 */
export const SESSION_AUTH: AuthStrategy = { kind: "session" };
